import { useEffect, useState } from 'react';
import { DEFAULT_LIBRARY } from '../components/RightMenu/constants';

export const useAssetPreloader = () => {
    // Return a map of { originalUrl: blobUrl }
    const [preloadedAssets, setPreloadedAssets] = useState<Record<string, string>>({});
    const [progress, setProgress] = useState(0);
    const [isComplete, setIsComplete] = useState(false);

    useEffect(() => {
        let mounted = true;
        const loadAssets = async () => {
            // 1. Gather all URLs from the library
            const urlsToLoad = new Set<string>();

            DEFAULT_LIBRARY.forEach(category => {
                category.items.forEach((item: any) => {
                    if (item.url) urlsToLoad.add(item.url);
                    if (item.syntheData && item.syntheData.clips) {
                        item.syntheData.clips.forEach((clip: any) => {
                            if (clip.url && !clip.url.startsWith('blob:')) urlsToLoad.add(clip.url);
                        });
                    }
                });
            });

            const total = urlsToLoad.size;
            if (total === 0) {
                setIsComplete(true);
                return;
            }

            let loadedCount = 0;
            const newPreloaded: Record<string, string> = {};

            // 2. Fetch and Cache
            const loadPromises = Array.from(urlsToLoad).map(async (url) => {
                try {
                    const cache = await caches.open('lvmh-media-cache-v1');
                    let response = await cache.match(url);

                    if (!response) {
                        response = await fetch(url);
                        if (response.ok) {
                            await cache.put(url, response.clone());
                        }
                    }

                    if (response && response.ok) {
                        const blob = await response.blob();
                        const blobUrl = URL.createObjectURL(blob);
                        newPreloaded[url] = blobUrl; // Map original URL to Blob URL
                    }
                } catch (e) {
                    console.warn(`[Preloader] Failed to preload ${url}`, e);
                } finally {
                    loadedCount++;
                    if (mounted) setProgress(Math.round((loadedCount / total) * 100));
                }
            });

            await Promise.all(loadPromises);

            if (mounted) {
                setPreloadedAssets(newPreloaded);
                setIsComplete(true);
                console.log(`[Preloader] Preloaded ${Object.keys(newPreloaded).length} media items into Blob URLs`);
            }
        };

        // Start loading in the background slightly delayed to not block initial render
        setTimeout(() => loadAssets(), 1000);

        return () => {
            mounted = false;
        };
    }, []);

    return { preloadedAssets, progress, isComplete };
};
